import ExecAPI from 'appRoot/api/api';

// API of the AI code-review pages (Controllers/CodeReviewData.cs and CodeReviewData.Fixes.cs).  Reads need CodeReviewRead, actions
// CodeReviewWrite, and fix actions CodeReviewApproveFixes.

/**
 * Header counts: open findings by severity and repository, pushes by status, poller status, repositories, canWrite, isAdmin.
 */
export function GetSummary()
{
	return ExecAPI("CodeReviewData/GetSummary");
}
/**
 * Findings for the queue, newest first.
 * @param {Object} filter { repo, statuses: [String], minSeverity, text, limit }
 */
export function ListFindings(filter)
{
	return ExecAPI("CodeReviewData/ListFindings", filter);
}
/**
 * A finding with its events, versions, comments, push, links and related findings.
 * @param {Number} id Finding ID.
 */
export function GetFinding(id)
{
	return ExecAPI("CodeReviewData/GetFinding", { id });
}
/**
 * Edits the title and description.  Refused if the finding is no longer at expectedVersion.
 */
export function EditFinding(id, title, description, expectedVersion)
{
	return ExecAPI("CodeReviewData/EditFinding", { id, title, description, expectedVersion });
}
/**
 * Adds a comment to a finding.
 */
export function AddComment(id, text)
{
	return ExecAPI("CodeReviewData/AddComment", { id, text });
}
/**
 * Edits a comment.
 */
export function EditComment(commentId, text)
{
	return ExecAPI("CodeReviewData/EditComment", { commentId, text });
}
/**
 * Rejects, marks already fixed, or reopens a finding.
 * @param {Number} id Finding ID.
 * @param {String} action "reject", "markFixed" or "reopen".
 * @param {String} rejectReason For reject: InvalidFinding, BehaviorIsIntentional, NotWorthFixing, Duplicate or Other.
 * @param {String} note Optional note.
 */
export function SetStatus(id, action, rejectReason, note)
{
	return ExecAPI("CodeReviewData/SetStatus", { id, action, rejectReason, note });
}
/**
 * Overrides the effective severity.
 */
export function SetSeverity(id, severity, note)
{
	return ExecAPI("CodeReviewData/SetSeverity", { id, severity, note });
}
/**
 * Builds the fix prompt for an external agent ({ markdown, fileName, headSha, headBranch, headError }).
 */
export function GeneratePrompt(id)
{
	return ExecAPI("CodeReviewData/GeneratePrompt", { id });
}
/**
 * Queues an AI re-evaluation of the finding at the default branch's head.  changed is false when one is already running at the same commit.
 */
export function ReEvaluate(id)
{
	return ExecAPI("CodeReviewData/ReEvaluate", { id });
}
/**
 * The finding's latest re-evaluation request and pending suggestion: { reEvaluation, pendingSuggestion, reviewsEnabled, autonomousFixesEnabled }.
 */
export function GetReEvaluationState(id)
{
	return ExecAPI("CodeReviewData/GetReEvaluationState", { id });
}
/**
 * Accepts the pending AI suggestion of a run ({ changed, openPrompt }).
 */
export function AcceptSuggestion(id, runId)
{
	return ExecAPI("CodeReviewData/AcceptSuggestion", { id, runId });
}
/**
 * Declines the pending AI suggestion of a run, with an optional note.
 */
export function DeclineSuggestion(id, runId, note)
{
	return ExecAPI("CodeReviewData/DeclineSuggestion", { id, runId, note });
}
/**
 * Approves a finding for a batched (immediate false) or immediate fix ({ changed, fixBatchId }).  Needs CodeReviewApproveFixes and codeReviewFixesEnabled.
 */
export function ApproveFix(id, immediate, note)
{
	return ExecAPI("CodeReviewData/SetStatus", { id, action: immediate ? "approveImmediate" : "approveBatch", note });
}
/**
 * Takes a fix approval back (also removes the finding from a fix batch that has not started).
 */
export function UnapproveFix(id, note)
{
	return ExecAPI("CodeReviewData/SetStatus", { id, action: "unapprove", note });
}
/**
 * The Fixes page: { fixesEnabled, reviewsEnabled, canApprove, requirePushApproval, schedule, scheduleError, nextScheduledAt, timeZone, model,
 * maxBudgetUsd, timeoutMinutes, maxFindingsPerBatch, commitAuthor, allowedTools, pollerStatus, approved: [{ repo, batch, immediate, inProgress }], batches }.
 */
export function GetFixes()
{
	return ExecAPI("CodeReviewData/GetFixes");
}
/**
 * "Run fix batch now" for one repository, or every repository when repo is empty ({ batches, message }).
 */
export function RunFixBatch(repo)
{
	return ExecAPI("CodeReviewData/RunFixBatch", { repo });
}
/**
 * A fix batch with its findings, outcomes, commits, build report, push state and runs ({ batch, fixesEnabled, canApprove, isAdmin }).
 */
export function GetFixBatch(id)
{
	return ExecAPI("CodeReviewData/GetFixBatch", { id });
}
/**
 * The full diff of a fix batch ({ found, diff, uncommittedDiff, truncated, bytes, fixHeadSha }).
 */
export function GetFixBatchDiff(id)
{
	return ExecAPI("CodeReviewData/GetFixBatchDiff", { id });
}
/**
 * "Approve and push" or "Retry push": pushes exactly the fix head the page showed.
 */
export function PushFixBatch(id, expectedHeadSha)
{
	return ExecAPI("CodeReviewData/PushFixBatch", { id, expectedHeadSha });
}
/**
 * Discards a fix batch (queued, awaiting push approval, or after a failed push); its findings go back to Open.
 */
export function DiscardFixBatch(id)
{
	return ExecAPI("CodeReviewData/DiscardFixBatch", { id });
}
/**
 * Reviewed pushes, newest first.
 * @param {Object} filter { status, repo, limit }
 */
export function ListTargets(filter)
{
	return ExecAPI("CodeReviewData/ListTargets", filter);
}
/**
 * A reviewed push with commits, runs and findings.
 * @param {String} id Target ID ("Repo:PushId").
 */
export function GetTarget(id)
{
	return ExecAPI("CodeReviewData/GetTarget", { id });
}
/**
 * Raw output of an agent run (administrators only): { found, resultJson, logTail }.
 */
export function GetRunOutput(runId)
{
	return ExecAPI("CodeReviewData/GetRunOutput", { runId });
}
/**
 * The code-review report for a period: { report: { fromDate, untilDate, lastDate, days, timeZone, costLabel, hasData, notes, total, repos } }.
 * @param {String} from First day, "YYYY-MM-DD" (server-local).
 * @param {String} until The day after the last day (exclusive), "YYYY-MM-DD".
 */
export function GetReport(from, until)
{
	return ExecAPI("CodeReviewData/GetReport", { from, until });
}
/**
 * URL of the same report as a self-contained HTML page (ReportData/CodeReviewReport).
 */
export function GetReportHtmlUrl(from, until)
{
	return appContext.appPath + "ReportData/CodeReviewReport?from=" + encodeURIComponent(from) + "&until=" + encodeURIComponent(until);
}
